// Inline sanitize preset — @github-ui/sanitize's `inline` preset for constrained
// inline text (github/react-platform#2264, epic github/react-platform#1723).
//
// Only inline formatting survives and every attribute is dropped. Excluded links
// and custom emoji keep their text fallback.

import type {Config, Purifier} from '../types'
import {sanitizeNewWindowLinks, sanitizeNonGitHubImageLinks, SHARED_CONFIG} from './shared-hooks'

export const INLINE_ALLOWED_TAGS: readonly string[] = [
  'br',
  'b',
  'i',
  'strong',
  'em',
  'code',
  'tt',
  'span',
  'ins',
  'del',
  'sup',
  'sub',
  'kbd',
  'q',
  'samp',
  'var',
  's',
  'strike',
  'ruby',
  'rt',
  'rp',
]

export const INLINE_SANITIZE_CONFIG: Readonly<Config> = {
  ...SHARED_CONFIG,
  // ALLOWED_TAGS overrides DOMPurify's defaults, so only the listed inline tags
  // are safe; every other element is unwrapped while KEEP_CONTENT preserves text.
  ALLOWED_TAGS: [...INLINE_ALLOWED_TAGS],
  ALLOWED_ATTR: [],
  ALLOW_ARIA_ATTR: false,
  KEEP_CONTENT: true,
}

function isImageElement(node: Node): node is Element {
  return node.nodeName.toLowerCase() === 'img'
}

function replaceImageWithAltText(node: Node): void {
  if (!isImageElement(node)) return

  node.replaceWith(node.ownerDocument.createTextNode(node.getAttribute('alt') ?? ''))
}

/**
 * Attach only the strict hooks relevant to the inline preset. Its empty
 * attribute allowlist leaves the class and data-* hooks with no permitted input
 * to protect. Image replacement happens before DOMPurify removes disallowed tags
 * so an image-only title keeps its alt text.
 */
export function addInlineSanitizeHooks(instance: Purifier): void {
  instance.addHook('uponSanitizeElement', replaceImageWithAltText)
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
  instance.addHook('afterSanitizeElements', sanitizeNonGitHubImageLinks)
}
